import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    collection,
    addDoc,
    doc,
    getDoc,
    updateDoc,
    getDocs,
    query,
    orderBy,
    where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
    ChevronLeft,
    Save,
    X,
    DollarSign,
    Calendar,
    MapPin,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    Building2,
    Plus,
    Edit3,
    Camera,
    Receipt,
    Tag,
    CreditCard,
    Sparkles,
    Zap,
    TrendingUp,
    Lightbulb,
    Clock,
} from "lucide-react";

const XFinanceExpenseForm = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [loadingMethods, setLoadingMethods] = useState(true);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [categories, setCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [recentExpenses, setRecentExpenses] = useState([]);
    const [suggestions, setSuggestions] = useState({ category: null, method: null, amount: null });

    const [formData, setFormData] = useState({
        descripcion: "",
        categoria: "",
        cantidad: "",
        metodoPago: "",
        fecha: new Date().toISOString().split('T')[0],
        ubicacion: "",
        notas: "",
        esRecurrente: false,
        frecuenciaRecurrencia: "mensual",
        fechaFin: "",
        etiquetas: [],
        comprobanteUrl: "",
    });

    const [comprobanteFile, setComprobanteFile] = useState(null);
    const [comprobantePreview, setComprobantePreview] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [newTag, setNewTag] = useState("");
    const [showQuickActions, setShowQuickActions] = useState(false);

    const frequencyOptions = [
        { value: "diario", label: "Diario" },
        { value: "semanal", label: "Semanal" },
        { value: "quincenal", label: "Quincenal" },
        { value: "mensual", label: "Mensual" },
        { value: "trimestral", label: "Trimestral" },
        { value: "semestral", label: "Semestral" },
        { value: "anual", label: "Anual" },
    ];

    // Palabras clave para categorización inteligente
    const categoryKeywords = {
        "Alimentación": ["supermercado", "comida", "restaurante", "cena", "almuerzo", "desayuno", "mercado", "tienda", "walmart", "soriana", "chedraui"],
        "Transporte": ["gasolina", "uber", "taxi", "metro", "transporte", "gasolinera", "pemex", "shell", "estacionamiento"],
        "Hogar": ["luz", "agua", "gas", "renta", "hipoteca", "cfe", "cable", "internet", "telefono"],
        "Salud": ["farmacia", "medico", "doctor", "hospital", "medicina", "farmacia", "clinica"],
        "Entretenimiento": ["cine", "netflix", "spotify", "disney", "hbo", "streaming", "pelicula", "juego"],
        "Educación": ["escuela", "universidad", "curso", "libro", "material", "colegiatura"],
        "Compras": ["ropa", "zapatos", "tienda", "amazon", "mercado libre", "compra"],
    };

    useEffect(() => {
        if (!user) {
            navigate("/login");
        }
    }, [user, navigate]);

    useEffect(() => {
        if (user) {
            loadCategories();
            loadPaymentMethods();
            loadRecentExpenses();
        }
    }, [user]);

    useEffect(() => {
        if (user && isEditing && id) {
            loadExpenseData();
        }
    }, [user, isEditing, id]);

    // Generar sugerencias inteligentes basadas en la descripción
    useEffect(() => {
        if (formData.descripcion && !isEditing) {
            generateSuggestions(formData.descripcion);
        }
    }, [formData.descripcion]);

    const loadCategories = async () => {
        if (!user) return;
        setLoadingCategories(true);
        try {
            const categoriesRef = collection(db, "expenses", user.uid, "expenseCategories");
            const q = query(categoriesRef, orderBy("nombre", "asc"));
            const querySnapshot = await getDocs(q);
            const categoriesData = [];
            querySnapshot.forEach((doc) => {
                categoriesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });
            setCategories(categoriesData);
        } catch (error) {
            console.error("Error al cargar categorías:", error);
            setMessage({ text: "Error al cargar las categorías", type: "error" });
        } finally {
            setLoadingCategories(false);
        }
    };

    const loadPaymentMethods = async () => {
        if (!user) return;
        setLoadingMethods(true);
        try {
            const methodsRef = collection(db, "expenses", user.uid, "paymentMethods");
            const q = query(methodsRef, orderBy("nombre", "asc"));
            const querySnapshot = await getDocs(q);
            const methodsData = [];
            querySnapshot.forEach((doc) => {
                methodsData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });
            setPaymentMethods(methodsData);
        } catch (error) {
            console.error("Error al cargar métodos de pago:", error);
            setMessage({ text: "Error al cargar los métodos de pago", type: "error" });
        } finally {
            setLoadingMethods(false);
        }
    };

    const loadRecentExpenses = async () => {
        if (!user) return;
        try {
            const expensesRef = collection(db, "expenses", user.uid, "userExpenses");
            const q = query(expensesRef, orderBy("createdAt", "desc"), orderBy("cantidad", "desc"));
            const querySnapshot = await getDocs(q);
            const expensesData = [];
            querySnapshot.forEach((doc) => {
                expensesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });
            setRecentExpenses(expensesData.slice(0, 10));
        } catch (error) {
            console.error("Error al cargar gastos recientes:", error);
        }
    };

    const generateSuggestions = (description) => {
        const descLower = description.toLowerCase();
        const newSuggestions = { category: null, method: null, amount: null };

        // Sugerir categoría basada en palabras clave
        for (const [category, keywords] of Object.entries(categoryKeywords)) {
            if (keywords.some(keyword => descLower.includes(keyword))) {
                const categoryExists = categories.find(cat => cat.nombre === category);
                if (categoryExists) {
                    newSuggestions.category = category;
                    break;
                }
            }
        }

        // Sugerir método de pago basado en gastos similares
        const similarExpenses = recentExpenses.filter(exp => 
            exp.descripcion.toLowerCase().includes(descLower.split(' ')[0]) ||
            descLower.includes(exp.descripcion.toLowerCase().split(' ')[0])
        );
        if (similarExpenses.length > 0) {
            const mostUsedMethod = similarExpenses.reduce((acc, exp) => {
                acc[exp.metodoPago] = (acc[exp.metodoPago] || 0) + 1;
                return acc;
            }, {});
            const suggestedMethod = Object.keys(mostUsedMethod).reduce((a, b) => 
                mostUsedMethod[a] > mostUsedMethod[b] ? a : b
            );
            newSuggestions.method = suggestedMethod;

            // Sugerir monto promedio
            const avgAmount = similarExpenses.reduce((sum, exp) => sum + exp.cantidad, 0) / similarExpenses.length;
            if (avgAmount > 0) {
                newSuggestions.amount = avgAmount.toFixed(2);
            }
        }

        setSuggestions(newSuggestions);
    };

    const applySuggestion = (type, value) => {
        if (type === "category") {
            setFormData(prev => ({ ...prev, categoria: value }));
        } else if (type === "method") {
            setFormData(prev => ({ ...prev, metodoPago: value }));
        } else if (type === "amount") {
            setFormData(prev => ({ ...prev, cantidad: value }));
        }
        setSuggestions(prev => ({ ...prev, [type]: null }));
    };

    const loadExpenseData = async () => {
        if (!user || !id) return;
        try {
            const expenseRef = doc(db, "expenses", user.uid, "userExpenses", id);
            const expenseSnap = await getDoc(expenseRef);
            if (expenseSnap.exists()) {
                const data = expenseSnap.data();
                setFormData({
                    ...data,
                    fecha: data.fecha || "",
                    fechaFin: data.fechaFin || "",
                    cantidad: data.cantidad ? data.cantidad.toString() : "",
                    etiquetas: data.etiquetas || [],
                    comprobanteUrl: data.comprobanteUrl || "",
                });
                if (data.comprobanteUrl) {
                    setComprobantePreview(data.comprobanteUrl);
                }
            } else {
                setMessage({ text: "Gasto no encontrado", type: "error" });
                navigate("/expenses");
            }
        } catch (error) {
            console.error("Error al cargar gasto:", error);
            setMessage({ text: "Error al cargar los datos", type: "error" });
        }
    };

    const uploadFile = async (file) => {
        if (!file || !user) return null;
        setUploadingFile(true);
        try {
            const timestamp = Date.now();
            const fileExtension = file.name.split('.').pop();
            const fileName = `comprobante_${timestamp}.${fileExtension}`;
            const fileRef = ref(storage, `expenses/${user.uid}/comprobantes/${fileName}`);
            const snapshot = await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error("Error al subir archivo:", error);
            setMessage({ text: "Error al subir el comprobante", type: "error" });
            return null;
        } finally {
            setUploadingFile(false);
        }
    };

    const deleteFile = async (fileUrl) => {
        if (!fileUrl) return;
        try {
            const fileRef = ref(storage, fileUrl);
            await deleteObject(fileRef);
        } catch (error) {
            console.error("Error al eliminar archivo:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            setMessage({ text: "Usuario no autenticado", type: "error" });
            return;
        }

        // Validaciones mejoradas
        if (parseFloat(formData.cantidad) <= 0) {
            setMessage({ text: "El monto debe ser mayor a cero", type: "error" });
            return;
        }

        if (formData.esRecurrente && !formData.frecuenciaRecurrencia) {
            setMessage({ text: "Debe seleccionar una frecuencia para gastos recurrentes", type: "error" });
            return;
        }

        setLoading(true);

        try {
            let comprobanteUrl = formData.comprobanteUrl;

            if (comprobanteFile) {
                if (isEditing && formData.comprobanteUrl) {
                    await deleteFile(formData.comprobanteUrl);
                }
                comprobanteUrl = await uploadFile(comprobanteFile);
                if (!comprobanteUrl) {
                    setLoading(false);
                    return;
                }
            }

            const expenseData = {
                ...formData,
                cantidad: parseFloat(formData.cantidad),
                comprobanteUrl: comprobanteUrl,
                updatedAt: new Date(),
                userId: user.uid,
            };

            delete expenseData.comprobante;

            if (isEditing) {
                const expenseRef = doc(db, "expenses", user.uid, "userExpenses", id);
                await updateDoc(expenseRef, expenseData);
                setMessage({ text: "Gasto actualizado correctamente", type: "success" });
            } else {
                const expensesRef = collection(db, "expenses", user.uid, "userExpenses");
                await addDoc(expensesRef, {
                    ...expenseData,
                    createdAt: new Date(),
                });
                setMessage({ text: "Gasto registrado correctamente", type: "success" });
            }

            setTimeout(() => {
                navigate("/expenses");
            }, 1500);
        } catch (error) {
            console.error("Error al guardar gasto:", error);
            setMessage({ text: "Error al guardar el gasto", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const addTag = () => {
        if (newTag.trim() && !formData.etiquetas.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                etiquetas: [...prev.etiquetas, newTag.trim()]
            }));
            setNewTag("");
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            etiquetas: prev.etiquetas.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
            if (!validTypes.includes(file.type)) {
                setMessage({ text: "Tipo de archivo no válido. Solo se permiten imágenes (JPG, PNG, GIF) y PDF", type: "error" });
                return;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                setMessage({ text: "El archivo es muy grande. Tamaño máximo: 5MB", type: "error" });
                return;
            }

            setComprobanteFile(file);

            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setComprobantePreview(e.target.result);
                };
                reader.readAsDataURL(file);
            } else {
                setComprobantePreview('pdf');
            }

            setMessage({ text: "Archivo seleccionado correctamente", type: "success" });
        }
    };

    const removeFile = () => {
        setComprobanteFile(null);
        setComprobantePreview(null);
        setFormData(prev => ({ ...prev, comprobanteUrl: "" }));
        const fileInput = document.getElementById('comprobante');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Obtener gastos similares para sugerencias rápidas
    const getSimilarExpenses = useMemo(() => {
        if (!formData.descripcion || formData.descripcion.length < 3) return [];
        const descLower = formData.descripcion.toLowerCase();
        return recentExpenses
            .filter(exp => exp.descripcion.toLowerCase().includes(descLower) || descLower.includes(exp.descripcion.toLowerCase().split(' ')[0]))
            .slice(0, 3);
    }, [formData.descripcion, recentExpenses]);

    const quickFillFromSimilar = (expense) => {
        setFormData(prev => ({
            ...prev,
            categoria: expense.categoria,
            metodoPago: expense.metodoPago,
            cantidad: expense.cantidad.toString(),
            ubicacion: expense.ubicacion || prev.ubicacion,
        }));
        setShowQuickActions(false);
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
            {/* Header Mejorado */}
            <div className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-30 shadow-sm">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 sm:py-6 gap-4">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate("/expenses")}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                                    {isEditing ? "Editar Gasto" : "Registrar Gasto"}
                                </h1>
                                <p className="text-slate-600 text-sm sm:text-base font-medium">
                                    {isEditing
                                        ? "Modifique la información de su gasto"
                                        : "Registre un nuevo gasto de forma rápida e inteligente"
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {/* Sugerencias Inteligentes */}
                    {(suggestions.category || suggestions.method || suggestions.amount) && !isEditing && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-semibold text-blue-900 mb-2">Sugerencias Inteligentes</h3>
                                    <p className="text-sm text-blue-700 mb-3">
                                        Basado en tus gastos anteriores, te sugerimos:
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {suggestions.category && (
                                            <button
                                                type="button"
                                                onClick={() => applySuggestion("category", suggestions.category)}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg text-sm font-medium transition-colors"
                                            >
                                                <Sparkles className="w-4 h-4" />
                                                Categoría: {suggestions.category}
                                            </button>
                                        )}
                                        {suggestions.method && (
                                            <button
                                                type="button"
                                                onClick={() => applySuggestion("method", suggestions.method)}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg text-sm font-medium transition-colors"
                                            >
                                                <CreditCard className="w-4 h-4" />
                                                Método: {suggestions.method}
                                            </button>
                                        )}
                                        {suggestions.amount && (
                                            <button
                                                type="button"
                                                onClick={() => applySuggestion("amount", suggestions.amount)}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg text-sm font-medium transition-colors"
                                            >
                                                <DollarSign className="w-4 h-4" />
                                                Monto: ${parseFloat(suggestions.amount).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSuggestions({ category: null, method: null, amount: null })}
                                    className="p-1 text-blue-400 hover:text-blue-600 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Gastos Similares - Quick Actions */}
                    {getSimilarExpenses.length > 0 && !isEditing && (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-5 h-5 text-yellow-500" />
                                    <h3 className="font-semibold text-slate-900">Gastos Similares</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowQuickActions(!showQuickActions)}
                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    {showQuickActions ? "Ocultar" : "Ver"}
                                </button>
                            </div>
                            {showQuickActions && (
                                <div className="space-y-2">
                                    {getSimilarExpenses.map((expense) => (
                                        <button
                                            key={expense.id}
                                            type="button"
                                            onClick={() => quickFillFromSimilar(expense)}
                                            className="w-full text-left p-3 hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-medium text-slate-900 text-sm">{expense.descripcion}</p>
                                                    <p className="text-xs text-slate-500">
                                                        {expense.categoria} • {expense.metodoPago} • ${expense.cantidad.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                                    </p>
                                                </div>
                                                <ChevronLeft className="w-4 h-4 text-slate-400 rotate-180" />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/50 shadow-lg">
                        <div className="p-6 sm:p-8">
                            {/* Información Básica */}
                            <div className="mb-6 sm:mb-8">
                                <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 sm:mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center shadow-sm">
                                        <Receipt className="w-5 h-5 text-white" />
                                    </div>
                                    Información del Gasto
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                    {/* Descripción */}
                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Descripción del Gasto *
                                        </label>
                                        <input
                                            type="text"
                                            name="descripcion"
                                            value={formData.descripcion}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium text-base"
                                            placeholder="Ej: Supermercado Walmart, Gasolina Shell, Cena restaurante..."
                                            required
                                            autoFocus
                                        />
                                        {formData.descripcion && formData.descripcion.length > 0 && (
                                            <p className="text-xs text-slate-500 mt-1">
                                                {formData.descripcion.length} caracteres
                                            </p>
                                        )}
                                    </div>

                                    {/* Monto */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Monto *
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="number"
                                                name="cantidad"
                                                step="0.01"
                                                min="0"
                                                value={formData.cantidad}
                                                onChange={handleInputChange}
                                                className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium text-lg"
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>
                                        {formData.cantidad && parseFloat(formData.cantidad) > 0 && (
                                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                                <CheckCircle className="w-3 h-3" />
                                                Monto válido
                                            </p>
                                        )}
                                    </div>

                                    {/* Fecha */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Fecha del Gasto *
                                        </label>
                                        <div className="relative">
                                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="date"
                                                name="fecha"
                                                value={formData.fecha}
                                                onChange={handleInputChange}
                                                max={new Date().toISOString().split('T')[0]}
                                                className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Categoría */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Categoría *
                                            {suggestions.category && (
                                                <span className="ml-2 text-xs text-blue-600 font-normal">
                                                    (Sugerida: {suggestions.category})
                                                </span>
                                            )}
                                        </label>
                                        <div className="flex gap-2">
                                            <select
                                                name="categoria"
                                                value={formData.categoria}
                                                onChange={handleInputChange}
                                                className={`flex-1 px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium ${suggestions.category && !formData.categoria ? 'border-blue-300 bg-blue-50' : 'border-slate-300'}`}
                                                required
                                                disabled={loadingCategories}
                                            >
                                                <option value="">
                                                    {loadingCategories ? "Cargando..." : "Seleccionar categoría"}
                                                </option>
                                                {categories.map(category => (
                                                    <option key={category.id} value={category.nombre}>
                                                        {category.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => navigate("/expense-categories")}
                                                className="px-4 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors flex items-center gap-2 font-medium"
                                                title="Gestionar categorías"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Método de Pago */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Método de Pago *
                                            {suggestions.method && (
                                                <span className="ml-2 text-xs text-blue-600 font-normal">
                                                    (Sugerido: {suggestions.method})
                                                </span>
                                            )}
                                        </label>
                                        <div className="flex gap-2">
                                            <select
                                                name="metodoPago"
                                                value={formData.metodoPago}
                                                onChange={handleInputChange}
                                                className={`flex-1 px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium ${suggestions.method && !formData.metodoPago ? 'border-blue-300 bg-blue-50' : 'border-slate-300'}`}
                                                required
                                                disabled={loadingMethods}
                                            >
                                                <option value="">
                                                    {loadingMethods ? "Cargando..." : "Seleccionar método"}
                                                </option>
                                                {paymentMethods.filter(method => method.activo).map(method => (
                                                    <option key={method.id} value={method.nombre}>
                                                        {method.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => navigate("/payment-methods")}
                                                className="px-4 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors flex items-center gap-2 font-medium"
                                                title="Gestionar métodos de pago"
                                            >
                                                <CreditCard className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Información Adicional */}
                            <div className="mb-6 sm:mb-8">
                                <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 sm:mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center shadow-sm">
                                        <MapPin className="w-5 h-5 text-white" />
                                    </div>
                                    Detalles Adicionales
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                    {/* Ubicación */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Ubicación
                                        </label>
                                        <div className="relative">
                                            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="text"
                                                name="ubicacion"
                                                value={formData.ubicacion}
                                                onChange={handleInputChange}
                                                className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                                                placeholder="Ej: Centro Comercial, Gasolinera..."
                                            />
                                        </div>
                                    </div>

                                    {/* Comprobante */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Comprobante
                                        </label>
                                        <div className="space-y-3">
                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    accept="image/*,.pdf"
                                                    onChange={handleFileUpload}
                                                    className="hidden"
                                                    id="comprobante"
                                                    disabled={uploadingFile}
                                                />
                                                <label
                                                    htmlFor="comprobante"
                                                    className={`w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium bg-white cursor-pointer flex items-center gap-2 hover:bg-slate-50 ${uploadingFile ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {uploadingFile ? (
                                                        <>
                                                            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                                                            Subiendo archivo...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Camera className="w-5 h-5 text-slate-400" />
                                                            {comprobanteFile || comprobantePreview
                                                                ? "Cambiar comprobante"
                                                                : "Adjuntar comprobante (JPG, PNG, PDF)"}
                                                        </>
                                                    )}
                                                </label>
                                            </div>

                                            {/* Preview del comprobante */}
                                            {comprobantePreview && (
                                                <div className="relative bg-slate-50 rounded-xl p-4 border-2 border-slate-200">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-center gap-3">
                                                            {comprobantePreview === 'pdf' ? (
                                                                <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                                                                    <FileText className="w-8 h-8 text-red-600" />
                                                                </div>
                                                            ) : (
                                                                <img
                                                                    src={comprobantePreview}
                                                                    alt="Preview del comprobante"
                                                                    className="w-16 h-16 object-cover rounded-lg border-2 border-slate-200"
                                                                />
                                                            )}
                                                            <div>
                                                                <p className="font-medium text-slate-900">
                                                                    {comprobanteFile ? comprobanteFile.name : "Comprobante actual"}
                                                                </p>
                                                                {comprobanteFile && (
                                                                    <p className="text-sm text-slate-500">
                                                                        {(comprobanteFile.size / 1024).toFixed(1)} KB
                                                                    </p>
                                                                )}
                                                                <p className="text-xs text-slate-400 mt-1">
                                                                    {comprobantePreview === 'pdf' ? 'Documento PDF' : 'Imagen'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={removeFile}
                                                            className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                                                            title="Eliminar archivo"
                                                        >
                                                            <X className="w-5 h-5" />
                                                        </button>
                                                    </div>

                                                    {/* Vista completa para imágenes */}
                                                    {comprobantePreview && comprobantePreview !== 'pdf' && (
                                                        <div className="mt-3">
                                                            <img
                                                                src={comprobantePreview}
                                                                alt="Vista completa del comprobante"
                                                                className="w-full max-h-64 object-contain rounded-lg border-2 border-slate-200 bg-white"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Etiquetas */}
                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Etiquetas
                                        </label>
                                        <div className="space-y-3">
                                            <div className="flex gap-2">
                                                <div className="relative flex-1">
                                                    <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                    <input
                                                        type="text"
                                                        value={newTag}
                                                        onChange={(e) => setNewTag(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                                                        placeholder="Agregar etiqueta y presiona Enter..."
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={addTag}
                                                    className="px-4 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-orange-600 transition-all shadow-sm"
                                                >
                                                    <Plus className="w-5 h-5" />
                                                </button>
                                            </div>
                                            {formData.etiquetas.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    {formData.etiquetas.map((tag, index) => (
                                                        <span
                                                            key={index}
                                                            className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-red-100 to-orange-100 text-red-800 rounded-full text-sm font-medium border border-red-200"
                                                        >
                                                            {tag}
                                                            <button
                                                                type="button"
                                                                onClick={() => removeTag(tag)}
                                                                className="hover:bg-red-200 rounded-full p-0.5 transition-colors"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Notas */}
                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Notas Adicionales
                                        </label>
                                        <div className="relative">
                                            <FileText className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                                            <textarea
                                                name="notas"
                                                value={formData.notas}
                                                onChange={handleInputChange}
                                                rows={4}
                                                className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                                                placeholder="Información adicional sobre el gasto..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Gasto Recurrente */}
                            <div className="mb-6 sm:mb-8">
                                <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 sm:mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-sm">
                                        <Calendar className="w-5 h-5 text-white" />
                                    </div>
                                    Configuración de Recurrencia
                                </h2>

                                <div className="space-y-4 sm:space-y-6">
                                    {/* Checkbox Recurrente */}
                                    <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-xl border border-purple-200">
                                        <input
                                            type="checkbox"
                                            id="esRecurrente"
                                            name="esRecurrente"
                                            checked={formData.esRecurrente}
                                            onChange={handleInputChange}
                                            className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                        />
                                        <label htmlFor="esRecurrente" className="text-sm font-semibold text-slate-700 cursor-pointer">
                                            Este es un gasto recurrente
                                        </label>
                                    </div>

                                    {formData.esRecurrente && (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 pl-4 sm:pl-8 border-l-4 border-purple-300 bg-purple-50/50 rounded-r-xl p-4 sm:p-6">
                                            {/* Frecuencia */}
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                    Frecuencia *
                                                </label>
                                                <select
                                                    name="frecuenciaRecurrencia"
                                                    value={formData.frecuenciaRecurrencia}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-medium"
                                                    required={formData.esRecurrente}
                                                >
                                                    {frequencyOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Fecha Fin */}
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                    Fecha de Fin (Opcional)
                                                </label>
                                                <input
                                                    type="date"
                                                    name="fechaFin"
                                                    value={formData.fechaFin}
                                                    onChange={handleInputChange}
                                                    min={formData.fecha}
                                                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-medium"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Resumen del Gasto */}
                            {formData.cantidad && parseFloat(formData.cantidad) > 0 && (
                                <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border-2 border-slate-200">
                                    <div className="flex items-center gap-2 mb-3">
                                        <TrendingUp className="w-5 h-5 text-slate-600" />
                                        <h3 className="font-semibold text-slate-900">Resumen</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-slate-600">Monto:</p>
                                            <p className="text-lg font-bold text-slate-900">
                                                ${parseFloat(formData.cantidad).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                        {formData.esRecurrente && (
                                            <div>
                                                <p className="text-slate-600">Frecuencia:</p>
                                                <p className="text-lg font-bold text-slate-900 capitalize">
                                                    {frequencyOptions.find(f => f.value === formData.frecuenciaRecurrencia)?.label}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-slate-200">
                                <button
                                    type="button"
                                    onClick={() => navigate("/expenses")}
                                    className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || uploadingFile || !formData.descripcion || !formData.categoria || !formData.cantidad || !formData.metodoPago}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                                >
                                    {loading || uploadingFile ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            {uploadingFile ? "Subiendo archivo..." : isEditing ? "Actualizando..." : "Guardando..."}
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            {isEditing ? "Actualizar Gasto" : "Registrar Gasto"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* Toast Message */}
            {message.text && (
                <div
                    className={`fixed top-4 right-4 p-4 rounded-xl shadow-xl transition-all duration-300 z-50 max-w-md ${message.type === "error"
                            ? "bg-red-50 border-2 border-red-200 text-red-800"
                            : "bg-green-50 border-2 border-green-200 text-green-800"
                        }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {message.type === "error" ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                            ) : (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                            <span className="font-semibold">{message.text}</span>
                        </div>
                        <button
                            onClick={() => setMessage({ text: "", type: "" })}
                            className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XFinanceExpenseForm;
